import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@ui/button';
import { useTranslation } from 'react-i18next';
import { Id as ToastId } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { ICustomerBase } from '@api/auth/session';
import { showUnexpectedError, showWarning } from '@lib/toast';
import CustomerInstanceSelect from '../../components/customer-instance-select-modal/customer-instance-select-modal';
import { MaintenanceMessage } from '@ui/planned-maintenance/maintenance-message';
import { useLoginContext } from './login.context';
import { Logo, RadioButtonCard } from '@tiltid-modern/ui-components';
import { Spinner } from '@tiltid-modern/ui-components';
import { useFrejaEIdAuth } from '../../hooks/useFrejaEIdAuth';
import { FrejaAuthType, FrejaEIDStatus } from '@api/auth/freja-eid';
import { isMobile } from 'react-device-detect';
import { IconFrejaLogo } from '@icons/freja-logo';
import { LogoLarge } from '@icons/logo-large';
import { useStorage } from '@lib/useStorage';
import { FREJA_AUTH_TYPE } from '@lib/constants/storage-keys';
import styles from './login.css';

export default function LoginFrejaEID() {
  const { t } = useTranslation();
  const { processing, setProcessing } = useLoginContext();
  const { getItem, setItem } = useStorage();
  const [authType, setAuthType] = useState<FrejaAuthType>(
    getItem(FREJA_AUTH_TYPE) ?? FrejaAuthType.Personal
  );

  const [isModalOpened, showModal] = useState(false);
  const toast = useRef<ToastId>('');

  useEffect(() => {
    setItem(FREJA_AUTH_TYPE, authType);
  }, [authType]);

  const onSelectCustomer = useCallback(
    (customers: ICustomerBase[], completeAuth: (customerId: number) => Promise<void>) => {
      if (customers.length === 0) {
        toast.current = showWarning(t('login:freja:noInstances'));
        setAuthData(undefined);
      } else if (customers.length === 1) {
        runSafe(async () => await completeAuth(customers[0].Id));
      } else {
        setCustomers(customers);
        showModal(true);
      }
    },
    []
  );

  const {
    authData,
    setAuthData,
    initiateAuth,
    cancelAuth,
    finishAuth,
    getStatusMessage,
    maintenanceInfo
  } = useFrejaEIdAuth(onSelectCustomer);

  const [customers, setCustomers] = useState<ICustomerBase[]>([]);

  async function runSafe(promise: () => Promise<any>) {
    setProcessing(true);
    try {
      await promise();
    } catch (error) {
      toast.current = showUnexpectedError(error);
    }
    setProcessing(false);
  }

  return (
    <>
      <div className={styles.logo}>
        <Logo img={<LogoLarge />} imageClassName={styles.tabsLogo} />
      </div>
      {authData?.qrCode && (
        <div className={styles.qrCodeWrapper}>
          <div
            className={styles.frejaCode}
            style={{ backgroundImage: `url(data:image/jpeg;base64,${authData.qrCode})` }}
          />
        </div>
      )}

      {authData?.status && (
        <p
          className={styles.authMessage}
          dangerouslySetInnerHTML={{ __html: getStatusMessage() }}
        />
      )}

      {(!authData?.authRef ||
        (authData.status &&
          (authData?.status === FrejaEIDStatus.Canceled ||
            authData?.status === FrejaEIDStatus.RP_Canceled ||
            authData?.status === FrejaEIDStatus.Rejected))) && (
        <>
          <p className={styles.frejaTitle}>{t('login:freja:chooseAuthType')}</p>
          <div className={styles.frejaCards}>
            <RadioButtonCard
              style={{ flex: 1 }}
              name="authType"
              id="personal-id"
              label={t('login:freja:personal')}
              checked={authType === FrejaAuthType.Personal}
              onChange={e => {
                if (e.currentTarget.checked) setAuthType(FrejaAuthType.Personal);
              }}
            />
            <RadioButtonCard
              style={{ flex: 1 }}
              name="access-control"
              id="organisation-id"
              label={t('login:freja:organisation')}
              checked={authType === FrejaAuthType.Organization}
              onChange={e => {
                if (e.currentTarget.checked) setAuthType(FrejaAuthType.Organization);
              }}
            />
          </div>
          <Button
            disabled={!authType}
            className={styles.frejaBtn}
            icon={<IconFrejaLogo />}
            text={t('login:freja:auth')}
            onClick={async () => {
              await runSafe(async () => await initiateAuth(authType, isMobile));
            }}
          />
        </>
      )}
      {authData?.authRef && authData?.status === FrejaEIDStatus.Expired && (
        <Button
          className={styles.frejaBtn}
          icon={<IconFrejaLogo />}
          text={t('login:freja:tryAgain')}
          onClick={async () => {
            await runSafe(async () => initiateAuth(authType, isMobile));
          }}
        />
      )}
      {authData?.qrCode && (
        <Button
          className={styles.authBtn}
          icon={<FontAwesomeIcon icon={faXmark} />}
          text={t('login:freja:cancel')}
          onClick={async () => {
            await runSafe(cancelAuth);
          }}
        />
      )}
      <MaintenanceMessage maintenance={maintenanceInfo} />
      <Suspense fallback={<Spinner />}>
        <CustomerInstanceSelect
          onRequestClose={() => {
            showModal(false);
            setAuthData(undefined);
          }}
          isBusy={processing}
          isOpen={isModalOpened}
          customers={customers}
          onCustomerSelect={async customer => {
            showModal(false);
            await runSafe(async () => finishAuth(customer.Id));
          }}
        />
      </Suspense>
    </>
  );
}
